import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ExternalLink } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Navbar, goToContact } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CASE_STUDIES } from "@/data/content";

export default function CaseStudyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const item = CASE_STUDIES.find((caseStudy) => caseStudy.slug === slug) || CASE_STUDIES[0];

  return (
    <div className="bg-[#14163C] min-h-screen">
      <Seo title={`${item.name} | Caso de éxito | Cibermedida`} description={item.result} />
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Link to="/casos-de-exito" className="inline-flex items-center gap-2 text-sm text-[#B9C3EC] hover:text-[#3FE0F2] transition-colors">
            <ArrowLeft size={16} /> Volver a casos de éxito
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center mt-12">
            <div>
              <p className="font-mono-cyber text-xs uppercase tracking-[0.22em] text-[#3FE0F2]">Caso de éxito · {item.sector}</p>
              <h1 className="mt-5 font-heading text-4xl sm:text-6xl font-semibold tracking-tight text-[#EEFBFF] leading-[1.05]">{item.name}</h1>
              <p className="mt-7 text-xl text-[#B9C3EC] leading-relaxed">{item.result}</p>
              <button onClick={() => goToContact(navigate, `Proyecto similar a ${item.name}`)} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#14163C] bg-[#3FE0F2] rounded-full px-7 py-3.5 hover:bg-[#7FEBF8] transition-colors group">
                Quiero un proyecto similar <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-[#3FE0F2]/20 h-[360px] sm:h-[480px]">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#14163C] via-transparent to-transparent" />
            </div>
          </div>

          <section className="grid md:grid-cols-3 gap-6 mt-20">
            {[["El reto", item.challenge], ["La solución", item.solution], ["El resultado", item.result]].map(([title, text]) => (
              <article key={title} className="bg-[#242764] border border-[#3FE0F2]/15 rounded-2xl p-7">
                <h2 className="font-heading text-xl font-semibold text-[#EEFBFF]">{title}</h2>
                <p className="mt-4 text-sm text-[#B9C3EC] leading-relaxed">{text}</p>
              </article>
            ))}
          </section>

          <section className="mt-20 border-t border-[#3FE0F2]/15 pt-12 grid lg:grid-cols-2 gap-12">
            <div>
              <p className="font-mono-cyber text-xs uppercase tracking-[0.2em] text-[#3FE0F2]">Qué aportó el proyecto</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold">Una solución pensada para el uso diario</h2>
              <ul className="mt-7 space-y-4">
                {item.stack.map((technology) => <li key={technology} className="flex items-center gap-3 text-[#D8E2FF]"><CheckCircle2 size={18} className="text-[#06D6A0]" />{technology}</li>)}
              </ul>
            </div>
            <div className="bg-[#1B1E4E] border border-[#3FE0F2]/15 rounded-2xl p-7 sm:p-9">
              <h2 className="font-heading text-2xl font-semibold">¿Tienes un reto parecido?</h2>
              <p className="mt-3 text-sm text-[#B9C3EC] leading-relaxed">Analizamos tu contexto y te proponemos una solución con alcance, prioridades y métricas claras.</p>
              <button onClick={() => goToContact(navigate, `Proyecto similar a ${item.name}`)} className="mt-6 inline-flex items-center gap-2 text-sm text-[#3FE0F2] hover:text-[#7FEBF8] transition-colors">Hablar con el equipo <ExternalLink size={15} /></button>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
